import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, DownloadIcon, FileTextIcon, HeadphonesIcon, SparklesIcon, XIcon } from 'lucide-react';
import { atnaQuestions, consents, invoices, policyDocs } from '../../data/profil';
import type { PanelId } from './AccountNav';
const titles: Record<PanelId, string> = {
  consentements: 'Consents',
  factures: 'My invoices',
  confidentialite: 'Privacy and transparency',
  atna: 'ATNA help'
};
function ConsentsPanel() {
  return <ul className="space-y-3" data-id="element-1720" data-mp="3135od2yign5i">
      {consents.map(consent => <li key={consent.id} className="rounded-2xl border border-slate-200 p-5" data-id="element-1721" data-mp="311kyvrr1zuei">
          <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-1722" data-mp="2x3wyi96ld9f8">
            <h3 className="text-[15px] font-bold text-ville-navy" data-id="element-1723" data-mp="2s27linzt6loi">{consent.title}</h3>
            <span className={`rounded-full px-3 py-1 text-xs font-bold ${consent.status === 'accorde' ? 'bg-ville-indigo-soft text-ville-indigo' : 'bg-amber-50 text-amber-800'}`} data-id="element-1724" data-mp="1lnoppq0kh1v4">
              {consent.status === 'accorde' ? 'Granted' : 'To confirm'}
            </span>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-slate-600" data-id="element-1725" data-mp="2h8bn5914crt6">{consent.purpose}</p>
          <p className="mt-2 text-xs text-slate-500" data-id="element-1726" data-mp="238ttogcvoqdh">{consent.grantedOn}</p>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2" data-id="element-1727" data-mp="287dg0hvdw46d">
            <button type="button" className="text-sm font-bold text-ville-indigo underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1728" data-mp="289hsvpb9gdis">
              View details
            </button>
            {consent.withdrawable ? <button type="button" className="text-sm font-semibold text-slate-500 underline underline-offset-4 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1729" data-mp="1kiu5dvov3fb4">
                Withdraw my consent
              </button> : <button type="button" className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-4 py-2 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1730" data-mp="1fiq0l2xbg90q">
                <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1731" data-mp="38fbrodgome8r" />
                Confirm
              </button>}
          </div>
        </li>)}
    </ul>;
}
function InvoicesPanel() {
  return <ul className="space-y-3" data-id="element-1732" data-mp="41dc320dde4y">
      {invoices.map(invoice => <li key={invoice.id} className="rounded-2xl border border-slate-200 p-5" data-id="element-1733" data-mp="4026cwnd5wb7">
          <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-1734" data-mp="tfq0nmeejv">
            <div className="min-w-0" data-id="element-1735" data-mp="3r5jrall6amk7">
              <p className="font-bold text-ville-navy" data-id="element-1736" data-mp="3r4i2zff5c3kj">{invoice.service}</p>
              <p className="text-sm text-slate-500" data-id="element-1737" data-mp="3m7rrl5eadw5g">
                {invoice.date} · {invoice.reference}
              </p>
            </div>
            <div className="text-right" data-id="element-1738" data-mp="2fotg0o1w74jq">
              <p className="text-lg font-extrabold text-ville-navy" data-id="element-1739" data-mp="2ara1ro572rrs">{invoice.amount}</p>
              <span className={`mt-1 inline-block rounded-full px-3 py-1 text-xs font-bold ${invoice.status === 'payee' ? 'bg-slate-100 text-slate-600' : 'bg-ville-indigo-soft text-ville-indigo'}`} data-id="element-1740" data-mp="20o4quxwwcogo">
                {invoice.status === 'payee' ? 'Paid' : 'To pay'}
              </span>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2" data-id="element-1741" data-mp="2fd0xqrn7vvfp">
            <button type="button" className="text-sm font-bold text-ville-indigo underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1742" data-mp="2jemi0y22zbzx">
              View the invoice
            </button>
            <button type="button" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1743" data-mp="2tdv8vi1ngai2">
              <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1744" data-mp="z03rfwd2wmc2" />
              Download
            </button>
          </div>
        </li>)}
    </ul>;
}
function PrivacyPanel() {
  return <ul className="space-y-3" data-id="element-1745" data-mp="2udoczn1vi9m5">
      {policyDocs.map(doc => <li key={doc.id} className="rounded-2xl border border-slate-200 p-5" data-id="element-1746" data-mp="2pfmt4zwmi69m">
          <div className="flex items-start gap-4" data-id="element-1747" data-mp="2phqupb71ut6q">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-100 text-ville-navy" aria-hidden="true" data-id="element-1748" data-mp="33fnushkpphq5">
              <FileTextIcon className="h-5 w-5" data-id="element-1749" data-mp="2yh45c76wtnt1" />
            </span>
            <div className="min-w-0" data-id="element-1750" data-mp="2apynjmheslg3">
              <h3 className="text-[15px] font-bold text-ville-navy" data-id="element-1751" data-mp="2ar05mawue87b">{doc.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-600" data-id="element-1752" data-mp="26qpyuw187dre">{doc.description}</p>
              <a href="#profil" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-ville-indigo hover:underline" data-id="element-1753" data-mp="2khbfsf74ddmn">
                Read the document
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1754" data-mp="1rzh09xfcii24" />
              </a>
            </div>
          </div>
        </li>)}
    </ul>;
}
function AtnaPanel() {
  return <div className="space-y-6" data-id="element-1755" data-mp="2j515srq18t6">
      <div className="flex items-start gap-3 rounded-2xl bg-ville-indigo-soft p-5" data-id="element-1756" data-mp="2ivb656of1nh">
        <SparklesIcon className="mt-0.5 h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-1757" data-mp="7adaypwyqw6v" />
        <p className="text-sm leading-relaxed text-ville-navy" data-id="element-1758" data-mp="3pocq9dht0xok">
          ATNA answers your questions about your account, your data and your services. Its answers are supervised by the Town and never replace a person.
        </p>
      </div>

      <div data-id="element-1759" data-mp="m7m33lu3c3zn">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500" data-id="element-1760" data-mp="m7vyjoxwn3cq">Common questions</h3>
        <ul className="mt-3 space-y-2" data-id="element-1761" data-mp="q6lgzvxsvb38">
          {atnaQuestions.map(question => <li key={question} data-id="element-1762" data-mp="c850xee1s88f">
              <button type="button" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-left text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1763" data-mp="cbaq0tmofa45">
                {question}
              </button>
            </li>)}
        </ul>
      </div>

      <div className="rounded-2xl border border-slate-200 p-5" data-id="element-1764" data-mp="2hxymg776ej1f">
        <div className="flex items-start gap-3" data-id="element-1765" data-mp="2dv2cf4vpbv93">
          <HeadphonesIcon className="mt-0.5 h-5 w-5 shrink-0 text-ville-navy" aria-hidden="true" data-id="element-1766" data-mp="293k9wce15mm0" />
          <p className="text-sm leading-relaxed text-slate-600" data-id="element-1767" data-mp="244z8i9n1kwvz">
            Would you rather speak to a person? A privacy officer can help you Monday to Friday.
          </p>
        </div>
        <button type="button" className="mt-4 inline-flex items-center justify-center rounded-full border border-ville-navy px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-navy hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1768" data-mp="1u6jg83ca3hkw">
          Contact an officer
        </button>
      </div>
    </div>;
}
const panels: Record<PanelId, () => JSX.Element> = {
  consentements: ConsentsPanel,
  factures: InvoicesPanel,
  confidentialite: PrivacyPanel,
  atna: AtnaPanel
};
interface AccountPanelProps {
  panel: PanelId | null;
  onClose: () => void;
}
export function AccountPanel({
  panel,
  onClose
}: AccountPanelProps) {
  const Content = panel ? panels[panel] : null;
  return <AnimatePresence data-id="element-1769" data-mp="q6sxmbr6irp">
      {panel && Content && <motion.div className="fixed inset-0 z-50 flex justify-end" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-1770" data-mp="5lvt9rvlk3p2">
          <button type="button" aria-label="Close the panel" onClick={onClose} className="absolute inset-0 bg-ville-navy/40" data-id="element-1771" data-mp="395qhw95e5f0y" />
          <motion.aside role="dialog" aria-modal="true" aria-label={titles[panel]} initial={{
        x: 40,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: 40,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative flex h-full w-full max-w-xl flex-col overflow-y-auto bg-white shadow-2xl" data-id="element-1772" data-mp="3i42hh0o8jn5j">
            <header className="sticky top-0 flex items-center justify-between gap-4 border-b border-slate-200 bg-white px-6 py-5" data-id="element-1773" data-mp="1ehbrsfh2dal2">
              <h2 className="text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-1774" data-mp="1t6zspflc2789">{titles[panel]}</h2>
              <button type="button" onClick={onClose} aria-label="Close" className="grid h-10 w-10 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1775" data-mp="1odf42e5mqplq">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-1776" data-mp="2c3j5cevcid9k" />
              </button>
            </header>
            <div className="px-6 py-6" data-id="element-1777" data-mp="2734xawtqv3bj">
              <Content data-id="element-1778" data-mp="hprq8zu8z16q" />
            </div>
          </motion.aside>
        </motion.div>}
    </AnimatePresence>;
}